import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import customFetchBase from '../expiredToken/expiredToken';
import {
  IEffectItemStore,
  IEffectsResponse
} from '../../types/effects/effectsStoreInterface';
import { IEffectRate } from '../../types/effects/effectsInterface';

const customFetch = customFetchBase(config.baseUrlDealers);

export const apiEffectsStore = createApi({
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getEffectsReadyForStore: builder.mutation<IEffectItemStore[], void>({
      query: () => ({
        url: 'api/Effect/ReadyForStore',
        method: 'GET'
      })
    }),
    getEffectsStoreList: builder.mutation<
      IEffectsResponse,
      { page: number; size: number; additionalParams: { [key: string]: any } }
    >({
      query: (data) => ({
        url: `api/Effect/ForStore?IncludeNotApproved=true`,
        method: 'GET',
        params: {
          page: data.page,
          size: data.size,
          ...data.additionalParams
        }
      })
    }),
    getEffectsListByLocation: builder.mutation<
      IEffectItemStore[],
      { locationId: number }
    >({
      query: (data) => ({
        url: `api/Effect/ByLocation/${data.locationId}`,
        method: 'GET'
      })
    }),
    deleteEffect: builder.mutation<null, number[]>({
      query: (ids) => ({
        url: `api/Effect?ids=${ids.join(',')}`,
        method: 'DELETE'
      })
    }),
    shareEffect: builder.mutation<null, { effectId: number; email: string }>({
      query: (data) => ({
        url: `api/Effect/ShareEffect`,
        method: 'POST',
        body: data
      })
    }),
    getLatestSharedEffectsUser: builder.mutation<
      { data: string[]; message: string },
      void
    >({
      query: () => ({
        url: `api/Effect/LatestSharedEffects`,
        method: 'GET'
      })
    }),
    reportEffect: builder.mutation<null, { effectId: number; reason: string }>({
      query: (data) => ({
        url: `api/Effect/ReportEffect`,
        method: 'POST',
        body: data
      })
    }),
    effectRating: builder.mutation<null, { effectId: number; rating: number }>({
      query: (data) => ({
        url: `api/Effect/Rating/Store`,
        method: 'POST',
        body: data
      })
    }),
    effectDefaultInLoc: builder.mutation<
      null,
      { effectId: number; enabled: boolean }
    >({
      query: (data) => ({
        url: `api/Effect/DefaultInLocation`,
        method: 'POST',
        body: data
      })
    }),
    effectUploadVideo: builder.mutation<null, { effectId: number; file: File }>(
      {
        query: ({ effectId, file }) => {
          const formData = new FormData();
          formData.append('EffectId', effectId.toString());
          formData.append('VideoFile', file);

          return {
            url: `api/Effect/ReadyForStore/UploadFile`,
            method: 'POST',
            body: formData
          };
        }
      }
    ),
    getEffectRatings: builder.mutation<{ data: IEffectRate[] }, number>({
      query: (effectId) => ({
        url: `api/Effect/Rating/${effectId}`,
        method: 'GET'
      })
    }),
    deleteRatingEffect: builder.mutation<
      null,
      { effectId: number; userId: number }
    >({
      query: (data) => ({
        url: `api/Effect/Rating/${data.effectId}/user/${data.userId}`,
        method: 'DELETE'
      })
    }),
    updateEffect: builder.mutation<
      null,
      {
        effectId: number;
        isHaven: boolean;
        name: string;
        tags: { id: number; tag: string }[];
      }
    >({
      query: (data) => ({
        url: `api/Effect/${data.effectId}`,
        method: 'PUT',
        body: {
          name: data.name,
          tags: data.tags,
          isHaven: data.isHaven
        }
      })
    }),
    putChangeVideoEffect: builder.mutation<
      null,
      { effectId: number; video: FormData }
    >({
      query: (data) => ({
        url: `api/Effect/Video/${data.effectId}`,
        method: 'PUT',
        body: data.video
      })
    }),
    getPendingEffectsStore: builder.mutation<any, {}>({
      query: () => ({
        url: `api/Effect/StoreRequests`,
        method: 'GET'
      })
    }),
    putChangeEffectStatus: builder.mutation<
      null,
      { effectId: number; status: number; deniedReason: string | null }
    >({
      query: (data) => ({
        url: 'api/Effect/PublishingStatus',
        method: 'PUT',
        body: {
          effectId: data.effectId,
          status: data.status,
          deniedReason: data.deniedReason
        }
      })
    }),
    banUser: builder.mutation<null, { effectId: number; banned: boolean }>({
      query: (data) => ({
        url: `api/Effect/Ban`,
        method: 'PUT',
        body: data
      })
    })
  })
});

export const {
  useGetEffectsReadyForStoreMutation,
  useGetEffectsStoreListMutation,
  useDeleteEffectMutation,
  useShareEffectMutation,
  useGetLatestSharedEffectsUserMutation,
  useReportEffectMutation,
  useEffectRatingMutation,
  useGetEffectRatingsMutation,
  useUpdateEffectMutation,
  useDeleteRatingEffectMutation,
  usePutChangeVideoEffectMutation,
  useEffectUploadVideoMutation,
  useGetPendingEffectsStoreMutation,
  usePutChangeEffectStatusMutation,
  useBanUserMutation,
  useEffectDefaultInLocMutation,
  useGetEffectsListByLocationMutation
} = apiEffectsStore;
